import { createListenerMiddleware, isRejectedWithValue } from '@reduxjs/toolkit';
import { useEffect, useEffectEvent } from 'react';

import { createLoggers } from '@highlight-cp/logger';

export interface RecheckTrigger {
  endpointName?: string;
}

type Recheck = (trigger: RecheckTrigger) => Promise<unknown>;

const { error: logError } = createLoggers({ tag: 'Session recheck' });

// A recheck that never settles must not leave the listener off for good.
const RECHECK_TIMEOUT_MS = 15_000;

// Shared with the current-user hooks, so the recheck and the error toast agree on an ended
// session. A 401 whose body is not JSON arrives as a PARSING_ERROR carrying originalStatus.
export function isUnauthorizedError(error: unknown) {
  const { status, originalStatus } = (error ?? {}) as {
    status?: unknown;
    originalStatus?: unknown;
  };

  return status === 401 || originalStatus === 401;
}

function isUnauthorizedResponse(action: unknown) {
  return isRejectedWithValue(action) && isUnauthorizedError(action.payload);
}

function endpointNameOf(action: unknown) {
  return (action as { meta?: { arg?: { endpointName?: string } } }).meta?.arg?.endpointName;
}

/**
 * Rechecks the session when an RTK request answers 401. Create one per app: the
 * store prepends `middleware`, and the auth provider calls `useSessionRecheck`.
 */
export function createSessionRecheck() {
  let registered: Recheck | undefined;

  const listener = createListenerMiddleware({
    onError: (error) => {
      // The name only: a message can carry what the request sent.
      logError('Session recheck failed', {
        errorName: error instanceof Error ? error.name : typeof error,
      });
    },
  });

  listener.startListening({
    predicate: isUnauthorizedResponse,
    effect: async (action, listenerApi) => {
      const recheck = registered;
      if (!recheck) {
        return;
      }

      // Take leading: 401s that arrive while this recheck runs are ignored.
      listenerApi.unsubscribe();
      try {
        // listenerApi.delay is tied to this effect: once the effect ends, it rejects quietly.
        await Promise.race([
          recheck({ endpointName: endpointNameOf(action) }),
          listenerApi.delay(RECHECK_TIMEOUT_MS),
        ]);
      } finally {
        listenerApi.subscribe();
      }
    },
  });

  function useSessionRecheck(recheck: Recheck) {
    // The latest render's recheck runs, without registering again on every render.
    const runRecheck = useEffectEvent(recheck);

    useEffect(() => {
      registered = (trigger) => runRecheck(trigger);

      return () => {
        registered = undefined;
      };
    }, []);
  }

  return { middleware: listener.middleware, useSessionRecheck };
}
